import { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Check, Loader2, Plus, Search, X } from 'lucide-react';
export default function InlineGiftAdder({ recipientId, onAdded, darkMode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [justAdded, setJustAdded] = useState(new Set());
  const allProductsRef = useRef(null);
  const inputRef = useRef(null);

  // Lazily load product catalogue once when opened
  useEffect(() => {
    if (!open || allProductsRef.current) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/products');
        allProductsRef.current = res.data || [];
      } catch (err) {
        logger.error(err);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Filter locally on every query change
  useEffect(() => {
    if (!allProductsRef.current) {
      setResults([]);
      return;
    }
    const all = allProductsRef.current;
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults(all.slice(0, 8));
      return;
    }
    const filtered = all.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.tags || []).some(t => (t || '').toLowerCase().includes(q))
    );
    setResults(filtered.slice(0, 12));
  }, [query, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleQuickAdd = async (product) => {
    setAdding(product.id);
    try {
      await api.post(`/recipients/${recipientId}/saved-gifts`, {
        product_id: product.id,
        notes: ''
      });
      setJustAdded(prev => new Set(prev).add(product.id));
      toast.success(`Added "${product.name}"`);
      if (onAdded) onAdded();
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to add gift');
    } finally {
      setAdding(null);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={`inline-add-gift-open-${recipientId}`}
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105"
        style={{
          background: darkMode?.isDark ? 'rgba(254, 215, 165, 0.15)' : 'rgba(158, 103, 82, 0.08)',
          color: darkMode?.isDark ? '#FED7A5' : '#9E6752',
          border: `1.5px dashed ${darkMode?.isDark ? '#FED7A5' : '#9E6752'}`
        }}
      >
        <Plus className="w-3.5 h-3.5" />
        Quick add
      </button>
    );
  }

  return (
    <div
      data-testid={`inline-add-gift-panel-${recipientId}`}
      className="rounded-xl p-3 transition-colors"
      style={{
        background: darkMode?.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${darkMode?.isDark ? '#3A3B45' : '#E5E7EB'}`
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: darkMode?.isDark ? '#A8A9A6' : '#9CA3AF' }}
          />
          <input
            ref={inputRef}
            data-testid={`inline-add-gift-search-${recipientId}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search gifts to add..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg transition-colors"
            style={{
              background: darkMode?.isDark ? '#1a1b23' : 'white',
              color: darkMode?.isDark ? '#FFFAF5' : '#20212B',
              border: `1px solid ${darkMode?.isDark ? '#3A3B45' : '#D1D5DB'}`
            }}
          />
        </div>
        <button
          data-testid={`inline-add-gift-close-${recipientId}`}
          onClick={() => { setOpen(false); setQuery(''); setJustAdded(new Set()); }}
          className="p-2 rounded-full hover:bg-black/10 transition-all"
          style={{ color: darkMode?.isDark ? '#A8A9A6' : '#73766A' }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6 gap-2" style={{ color: darkMode?.isDark ? '#A8A9A6' : '#73766A' }}>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading products...</span>
        </div>
      )}

      {!loading && results.length === 0 && (
        <p className="text-xs text-center py-4" style={{ color: darkMode?.isDark ? '#A8A9A6' : '#73766A' }}>
          {query ? 'No matches, try another search term' : 'Start typing to search our catalogue'}
        </p>
      )}

      {!loading && results.length > 0 && (
        <ul
          className="space-y-1 max-h-64 overflow-y-auto pr-1"
          style={{ overscrollBehavior: 'contain' }}
        >
          {results.map(p => {
            const added = justAdded.has(p.id);
            return (
              <li key={p.id}>
                <button
                  data-testid={`inline-add-gift-item-${p.id}`}
                  onClick={() => !added && handleQuickAdd(p)}
                  disabled={adding === p.id || added}
                  className="w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all hover:scale-[1.01]"
                  style={{
                    background: darkMode?.isDark ? 'rgba(255,255,255,0.03)' : 'white',
                    border: `1px solid ${darkMode?.isDark ? '#3A3B45' : '#E5E7EB'}`,
                    opacity: added ? 0.6 : 1
                  }}
                >
                  {p.image_url && (
                    <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-white">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: darkMode?.isDark ? '#FFFAF5' : '#20212B' }}>
                      {p.name}
                    </p>
                    <p className="text-[11px]" style={{ color: darkMode?.isDark ? '#A8A9A6' : '#73766A' }}>
                      ${p.price}{p.category ? ` · ${p.category}` : ''}
                    </p>
                  </div>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: added ? '#10B981' : '#9E6752',
                      color: 'white'
                    }}
                  >
                    {adding === p.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : added ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
